import {
  BREAKPOINTS_WIDTHS,
  formatMoney,
  template,
  useWindowSize,
} from '@belong/common';
import { Button, Text } from '@belong/ui';
import PaymentMethodModel from 'models/common/PaymentMethod';
import { PaymentMethod } from 'models/enums';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { PAY_AS_YOU_GO_STRINGS } from '../strings';
import { usePaygAmounts } from '../use-payg-amounts';

const STRINGS = PAY_AS_YOU_GO_STRINGS.processingStep;

type TextVariant = 'p1' | 'body';

type TimelineRowProps = {
  detail: string;
  index: number;
  isActive: boolean;
  isDone?: boolean;
  isMobile: boolean;
  status?: string;
  title: string;
  variant: TextVariant;
};

function TimelineRow({
  detail,
  index,
  isActive,
  isDone = false,
  isMobile,
  status,
  title,
  variant,
}: TimelineRowProps) {
  const badgeClassName =
    (isDone && 'border-green bg-green text-white') ||
    (isActive && 'border-blue bg-blue text-white') ||
    'border-gray text-navy';
  const statusClassName =
    (isDone && 'text-green') || (isActive && 'text-blue') || 'text-dark-gray';

  return (
    <div className="flex gap-xs">
      <span
        className={`flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full border border-solid ${badgeClassName}`}
      >
        <Text as="span" fontWeight="semibold">
          {index}
        </Text>
      </span>
      <div
        className={`flex flex-1 justify-between gap-sm ${
          isMobile ? 'items-start' : 'items-center'
        }`}
      >
        <div className="flex flex-col gap-2xs">
          <Text fontWeight="semibold" variant={variant}>
            {title}
          </Text>
          <Text className="text-dark-gray" variant="p1">
            {detail}
          </Text>
        </div>
        {status && (
          <Text
            className={`whitespace-nowrap ${statusClassName}`}
            fontWeight="semibold"
            variant={variant}
          >
            {status}
          </Text>
        )}
      </div>
    </div>
  );
}

function TimelineConnector() {
  return (
    <span className="ml-[17px] h-sm w-0 border-l border-dashed border-gray" />
  );
}

type Props = {
  isReportConfirmed: boolean;
  isUpfrontCompleted: boolean;
  onDone: () => void;
  paymentMethod: PaymentMethodModel;
};

export function PaygProcessingStep({
  isReportConfirmed,
  isUpfrontCompleted,
  onDone,
  paymentMethod,
}: Props) {
  const { upfront, remaining, fee, totalDueNow } = usePaygAmounts();
  const { width } = useWindowSize();

  const isMobile = width <= BREAKPOINTS_WIDTHS.SM;
  const rowVariant: TextVariant = isMobile ? 'p1' : 'body';
  const hasCardFee = fee > 0;

  const methodLabel =
    paymentMethod.paymentMethod === PaymentMethod.Ach
      ? paymentMethod.achDetails?.bankName
      : paymentMethod.creditCardDetails?.cardType;

  const title = isUpfrontCompleted ? STRINGS.completedTitle : STRINGS.title;
  const subtitle =
    (!isReportConfirmed && STRINGS.unconfirmedSubtitle) ||
    (isUpfrontCompleted && STRINGS.completedSubtitle) ||
    STRINGS.subtitle;

  return (
    <div className="flex w-full flex-col items-center pt-2sm sm:pt-2xl">
      <div className="flex w-full flex-col items-center gap-xl sm:gap-2xl md:px-xl">
        <div className="flex flex-col items-center gap-xs text-center sm:max-w-[340px] md:max-w-[700px]">
          <Text fontWeight="semibold" variant="h2">
            {title}
          </Text>
          <Text variant={rowVariant}>
            {template(subtitle, {
              total: formatMoney(totalDueNow, 'DOLLARS_WITH_CENTS'),
              method: methodLabel,
              digits: paymentMethod.lastDigits,
            })}
          </Text>
        </div>

        <div className="flex w-full flex-col gap-sm">
          <Text fontWeight="semibold">{STRINGS.howItWorks}</Text>
          <div className="flex flex-col gap-2xs">
            <TimelineRow
              detail={template(
                hasCardFee
                  ? STRINGS.upfrontDetailWithFee
                  : STRINGS.upfrontDetail,
                { amount: formatMoney(upfront, 'DOLLARS_WITH_CENTS') },
              )}
              index={1}
              isActive={!isUpfrontCompleted}
              isDone={isUpfrontCompleted}
              isMobile={isMobile}
              status={
                isUpfrontCompleted
                  ? STRINGS.upfrontCompletedStatus
                  : STRINGS.upfrontStatus
              }
              title={template(STRINGS.upfrontTitle, {
                total: formatMoney(totalDueNow, 'DOLLARS_WITH_CENTS'),
              })}
              variant={rowVariant}
            />
            <TimelineConnector />
            <TimelineRow
              detail={STRINGS.inProgressDetail}
              index={2}
              isActive={isUpfrontCompleted}
              isMobile={isMobile}
              status={STRINGS.inProgressStatus}
              title={STRINGS.inProgressTitle}
              variant={rowVariant}
            />
            <TimelineConnector />
            <TimelineRow
              detail={template(
                hasCardFee
                  ? STRINGS.completeDetailWithFee
                  : STRINGS.completeDetail,
                { remaining: formatMoney(remaining, 'DOLLARS_WITH_CENTS') },
              )}
              index={3}
              isActive={false}
              isMobile={isMobile}
              title={STRINGS.completeTitle}
              variant={rowVariant}
            />
          </div>
        </div>
      </div>

      <div className="mt-xl w-full pb-xl sm:mt-2xl md:pb-2xl">
        <QuestionsOrConcernsModalFooter bareOnDesktop>
          <Button
            className="md:w-[260px]"
            onClick={onDone}
            size={{ _: 'fluid' }}
          >
            {STRINGS.cta}
          </Button>
        </QuestionsOrConcernsModalFooter>
      </div>
    </div>
  );
}
